/**
 * RepConnect - NPI Linking View
 * 
 * Allows healthcare providers to search for and link their NPI profiles
 * to their MeetingSync user accounts for verification.
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 */

import React, { useState, useEffect } from 'react';
import { 
  Search, User, Badge, MapPin, Phone, Mail, Building, 
  CheckCircle, AlertCircle, Shield, Loader, Star,
  ArrowRight, Clock, AlertTriangle, Info
} from 'lucide-react';
import NPILinking from '../utils/npiLinking.js';
import { UserStorage } from '../utils/userStorage.js';
import { logger } from '../utils/logger';

const NPILinkingView = ({ currentUser, onLinkingComplete, setCurrentView }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [verificationStep, setVerificationStep] = useState('search'); // search, verify, processing, complete
  const [verificationData, setVerificationData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Pre-fill search with user's name if available
    if (currentUser && currentUser.fullName) {
      setSearchQuery(currentUser.fullName);
    }
  }, [currentUser]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError('');

    try {
      // Simple NPI API search - customer is searching for themselves
      const { NPIAPI } = await import('../utils/npiAPI');

      const query = searchQuery.trim();
      let results = [];

      logger.debug('NPI linking search query', { query });

      // Check if search query is a 10-digit NPI number
      if (/^\d{10}$/.test(query.replace(/\D/g, ''))) {
        const npiNumber = query.replace(/\D/g, '');
        logger.debug('Searching by exact NPI number', { npiNumber });

        const exactMatch = await NPIAPI.getProviderByNPI(npiNumber);
        if (exactMatch) {
          results = [exactMatch];
        }
      } else {
        // Search by name
        logger.debug('Searching by name', { name: query });

        const searchResults = await NPIAPI.searchProviders({
          name: query,
          specialty: currentUser?.specialty || 'all',
          limit: 20
        });

        results = searchResults.providers || [];
      }

      logger.debug('NPI linking search results', { count: results.length });

      setSearchResults(results);

      if (results.length === 0) {
        setError('No NPI profiles found. Please try a different search term or verify your name spelling.');
      }
    } catch (error) {
      logger.error('Search error', { error: error.message });
      setError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectProfile = async (profile) => {
    setSelectedProfile(profile);
    setIsProcessing(true);
    setError('');

    try {
      // Get NPI from profile (NPI API returns 'npi' field)
      const npiNumber = profile.npi;

      if (!npiNumber) {
        setError('This profile does not have an NPI number associated with it.');
        setIsProcessing(false);
        return;
      }

      const result = await NPILinking.initiateNPIVerification(
        currentUser.id,
        {
          fullName: currentUser.fullName || currentUser.name,
          specialty: currentUser.specialty,
          email: currentUser.email,
          practiceAddress: currentUser.practiceAddress || ''
        },
        npiNumber,
        profile
      );

      if (result.success) {
        // Auto-verified
        setVerificationStep('complete');
        setVerificationData(result);
        if (onLinkingComplete) {
          onLinkingComplete(result.npiLink);
        }
      } else if (result.requiresVerification) {
        // Needs manual verification
        setVerificationStep('verify');
        setVerificationData(result);
      }
    } catch (error) {
      logger.error('Verification error:', error);
      setError(error.message);
      setSelectedProfile(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCompleteVerification = async () => {
    if (!verificationData?.verificationId) return;

    setIsProcessing(true);
    
    try {
      const result = await NPILinking.completeNPIVerification(
        verificationData.verificationId,
        { method: 'manual_confirmation' }
      );

      if (result.success) {
        setVerificationStep('complete');
        setVerificationData(result);
        if (onLinkingComplete) {
          onLinkingComplete(result.npiLink);
        }
      }
    } catch (error) {
      logger.error('Completion error:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderSearchStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Link Your NPI Profile</h2>
        <p className="text-gray-600">
          Verify your identity by linking your MeetingSync account with your official NPI profile.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">Why Link Your NPI?</h3>
            <ul className="text-sm text-blue-700 mt-1 space-y-1">
              <li>• Verify your professional identity</li>
              <li>• Manage your profile information</li>
              <li>• Control how sales reps see your details</li>
              <li>• Access provider-only features</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search for Your NPI Profile
          </label>
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter your name or 10-digit NPI number"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-2"
            >
              {isSearching ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              <span>{isSearching ? 'Searching...' : 'Search'}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-medium text-gray-800">Select Your Profile:</h3>
            {searchResults.map((profile) => (
              <div
                key={profile.npi}
                className="border border-gray-300 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleSelectProfile(profile)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-800">{profile.name}</h4>
                      <Badge className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-gray-600 mb-1">{profile.specialty}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Building className="w-4 h-4" />
                        <span>{profile.hospital || profile.organization || 'Private Practice'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>{profile.city}, {profile.state}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">NPI: {profile.npi}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderVerificationStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Confirm Your NPI Profile</h2>
        <p className="text-gray-600">
          Please confirm that this is your NPI profile.
        </p>
      </div>

      {selectedProfile && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <User className="w-10 h-10 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{selectedProfile.name}</h3>
              <p className="text-gray-600">{selectedProfile.specialty}</p>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <Building className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">{selectedProfile.hospital || selectedProfile.organization || 'Private Practice'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">{selectedProfile.city}, {selectedProfile.state}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Badge className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">NPI: {selectedProfile.npi}</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-2">By clicking "Confirm & Link", you certify that:</h4>
        <ul className="text-sm text-gray-700 space-y-1.5">
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>You are the healthcare provider listed in this NPI profile</span>
          </li>
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>This NPI number will be linked to your RepConnect account</span>
          </li>
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>Your profile information will be updated with verified NPI data</span>
          </li>
        </ul>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => {
            setVerificationStep('search');
            setSelectedProfile(null);
            setVerificationData(null);
          }}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleCompleteVerification}
          disabled={isProcessing}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2"
        >
          {isProcessing ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <CheckCircle className="w-5 h-5" />
          )}
          <span>{isProcessing ? 'Linking...' : 'Confirm & Link'}</span>
        </button>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6 text-center">
      <div>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">NPI Successfully Linked!</h2>
        <p className="text-gray-600">
          Your NPI number has been linked to your RepConnect account.
        </p>
      </div>

      {selectedProfile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <User className="w-8 h-8 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">{selectedProfile.name}</h3>
              <p className="text-green-700">{selectedProfile.specialty}</p>
            </div>
            <div className="flex items-center space-x-1">
              <Badge className="w-5 h-5 text-green-600" />
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <p className="text-sm font-medium text-green-700">NPI: {selectedProfile.npi}</p>
            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">Verified</span>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2 flex items-center justify-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>What Happened</span>
        </h4>
        <ul className="text-sm text-blue-700 space-y-1.5 text-left">
          <li>✓ Your NPI number has been added to your profile</li>
          <li>✓ Your profile has been updated with verified NPI data (specialty, location, etc.)</li>
          <li>✓ A link icon now appears next to your NPI in your profile settings</li>
          <li>✓ Sales reps can now find your verified RepConnect profile</li>
        </ul>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-2">Next Steps</h4>
        <p className="text-sm text-gray-700 mb-2">
          You can view and manage your linked NPI in the Profile tab.
        </p>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => window.location.reload()}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          View My Profile
        </button>
        <button
          onClick={() => setCurrentView('dashboard')}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );

  if (!currentUser) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Please log in to link your NPI profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        {verificationStep === 'search' && renderSearchStep()}
        {verificationStep === 'verify' && renderVerificationStep()}
        {verificationStep === 'complete' && renderCompleteStep()}
      </div>
    </div>
  );
};

export default NPILinkingView;